import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Copy, Crosshair, RotateCw, Check } from 'lucide-react';
import { formatCoordinate, COORDINATE_FORMATS } from '@/lib/coordinates';

const MENU_WIDTH = 240;
const MENU_HEIGHT_ESTIMATE = 180;
const FORMAT_KEYS = COORDINATE_FORMATS.map((f) => f.value); // ['decimal','dms','mgrs','pluscode']

export default function MapContextMenu({
  x,
  y,
  lngLat,
  onClose,
  onAddPOI,
  onCopyCoords, // legacy callback — retained but no longer used (copy is local now)
  onCenterHere,
}) {
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const [formatIdx, setFormatIdx] = useState(0);
  const [justCopied, setJustCopied] = useState(false);
  const currentFormat = FORMAT_KEYS[formatIdx];
  const formattedCoord = formatCoordinate(lngLat?.lat, lngLat?.lng, currentFormat);

  const cycleFormat = useCallback(() => {
    setFormatIdx((i) => (i + 1) % FORMAT_KEYS.length);
  }, []);

  const copyInCurrentFormat = useCallback(async () => {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(formattedCoord);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 1200);
      // Notify the legacy callback for any host that wants to act on the copy
      // (e.g. log analytics). It's optional and we no longer hide on copy so
      // the user can switch format + copy again without reopening the menu.
      onCopyCoords?.(lngLat);
    } catch { /* ignore */ }
  }, [formattedCoord, lngLat, onCopyCoords]);

  const clampedX = Math.min(x, window.innerWidth - MENU_WIDTH - 8);
  const clampedY = Math.min(y, window.innerHeight - MENU_HEIGHT_ESTIMATE - 8);

  const handleClose = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    function onKeyDown(e) { if (e.key === 'Escape') handleClose(); }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  useEffect(() => {
    function onPointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) handleClose();
    }
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [handleClose]);

  const actions = [
    {
      icon: MapPin,
      label: t('map.addPoiHere', 'Add POI here'),
      onClick: () => { onAddPOI?.(lngLat); handleClose(); },
    },
    {
      icon: Crosshair,
      label: t('map.centerHere', 'Center here'),
      onClick: () => { onCenterHere?.(lngLat); handleClose(); },
    },
  ];

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[220px] bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-xl shadow-xl dark:shadow-black/50 border border-slate-200/60 dark:border-slate-700/60 py-1 overflow-hidden"
      style={{ left: clampedX, top: clampedY }}
    >
      {/* Format-aware coordinate display + cycle/copy controls. The cycle
          button advances Decimal → DMS → MGRS → Plus Code; copy lifts the
          currently-shown text to the clipboard (not the user's saved
          preference). Single source of formatting: src/lib/coordinates.js. */}
      <div className="px-3 py-2 border-b border-slate-200/60 dark:border-slate-700/60">
        <div className="flex items-center gap-1.5">
          <p className="text-[11px] font-mono text-slate-600 dark:text-slate-300 truncate text-start flex-1" dir="ltr" title={formattedCoord}>
            {formattedCoord}
          </p>
          <button
            type="button"
            onClick={cycleFormat}
            title={t('map.cycleCoordinateFormat', 'Cycle format')}
            className="p-1 rounded hover:bg-slate-200/60 dark:hover:bg-slate-700/60 text-slate-500 dark:text-slate-400"
          >
            <RotateCw size={12} />
          </button>
          <button
            type="button"
            onClick={copyInCurrentFormat}
            title={t('map.copyInCurrentFormat', 'Copy in current format')}
            className="p-1 rounded hover:bg-slate-200/60 dark:hover:bg-slate-700/60 text-slate-500 dark:text-slate-400"
          >
            {justCopied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
          </button>
        </div>
        <p className="text-[9px] text-slate-400 dark:text-slate-500 uppercase tracking-wider mt-0.5">
          {t(`coordinates.${currentFormat === 'pluscode' ? 'plusCode' : currentFormat}`, currentFormat)}
        </p>
      </div>
      <div className="py-1">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={action.onClick}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-start text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
          >
            <action.icon size={15} className="flex-shrink-0 text-slate-500 dark:text-slate-400" />
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
}
